import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Column } from '@tanstack/react-table';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Settings } from 'lucide-react';
import { Button } from '@curtin-dxp/curtin-ui';
import { useAwmsStore } from '@/store';
import { Input } from '@/components/ui/input';
import { SearchFilterDropDown } from '../SearchFilterDropDown';
import { FilterOption } from '../DataTable';
import { useIsAdmin } from '@/hooks/use-is-admin';

interface GlobalFilterHeaderProps<TData> {
  column?: Column<TData, unknown>;
  placeHolder?: string;
  filterOptions?: FilterOption[];
}

interface SearchFilterState {
  [key: string]: any;
}

export const GlobalFilter = <TData,>({ column, placeHolder, filterOptions }: GlobalFilterHeaderProps<TData>) => {
  const { data: isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const searchFilterState: SearchFilterState = useAwmsStore((state) => state.searchFilters);
  const setColumnFilters = useAwmsStore((state) => state.setColumnFilterState);
  const columnFilters = useAwmsStore((state) => state.columnFilters);

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const filterValue = event.target.value;
    column?.setFilterValue(filterValue);
    setColumnFilters({ ...columnFilters, [column?.id ?? '']: filterValue });
  };

  useEffect(() => {
    if (column && columnFilters[column.id] != null) {
      column.setFilterValue(columnFilters[column.id] as string);
    }
  }, [column, columnFilters]);

  return (
    <div className="flex w-screen flex-wrap items-center gap-5 border-b-[1px] border-grey-10 px-8 py-5 min-[1236px]:flex-row">
      <div className="relative flex items-center">
        <Input
          placeholder={placeHolder}
          value={(columnFilters[column?.id ?? ''] as string) ?? ''}
          onChange={onChangeHandler}
          className="h-10 w-[250px] bg-white px-3 pl-5 pr-9 text-base font-normal leading-6 placeholder:text-grey-600 hover:border-deep-blue
            focus-visible:ring-deep-blue"
          aria-label="Search by id or name"
        />
        <MagnifyingGlassIcon className="absolute right-3 h-[18px] w-[18px]" />
      </div>

      {filterOptions &&
        filterOptions.map((filter) => {
          const filterValue = searchFilterState[filter.stateFilterFieldName];
          return (
            <SearchFilterDropDown
              key={filter.stateFilterFieldName}
              placeholder={filter.placeholder}
              checkbox={filter.checkbox}
              options={filter.options}
              stateFilterFieldName={filter.stateFilterFieldName}
              stateFilterValue={filterValue}
              selectedState={filter.selectedState}
              setSelectedState={filter.setSelectedState}
              className={filter.className}
            />
          );
        })}

      {isAdmin && (
        <div className="ml-auto flex items-center">
          <Button
            variant="outline-secondary"
            className="h-10 border-black bg-black text-white hover:bg-transparent hover:text-black focus:outline-none focus-visible:ring-1
              focus-visible:ring-inset focus-visible:ring-white focus-visible:ring-offset-0"
            aria-label="Admin View"
            onClick={() => navigate('/admin')}
          >
            Admin View
            <Settings className="ml-[10px] h-6 w-6" />
          </Button>
        </div>
      )}
    </div>
  );
};
